import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {createRef, ref} from 'lit/directives/ref.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import store from '../../../store';

import {getValueOfFeature, hasOnlyThisFeatureValue, pimText, pimTextDebug} from '../../../util/values';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {conditional} from '../../../util/conditional';
import {
	selectArticleCodeJoinedComponents,
	selectDimensionHeight,
	selectDimensionWidth,
	selectDimensionDepth,
	selectWeight,
	selectWindsurfacearea,
	selectVariantCodeTypes,
	selectSpcConfiguration,
	selectDimensionDiameter,
	selectDimmLevel,
	selectCoefficients,
	selectHasCoefficients,
	selectSectionConfig, selectDimensionLength, selectRegion
} from '../../../selectors';
import {calculateCurrent, calculatePower} from '../../../util/dimLevel';
import {saveTenderText} from '../../../slices/configurationSlice';

@customElement('general-tender-text')
export class GeneralTenderText extends connect(store)(LitElement) {

	static styles = css`
		:host {
			container-type: inline-size;
			font: var(--font-specsheet-print-small-text);
		}

		p {
			margin: 0 0 1cqw 0;
			font: var(--font-specsheet-print-small-text);
			font-size: 0.92cqw;
			line-height: 140%;
		}

		.info {
			font: var(--font-specsheet-print-small-text);
			font-size: 0.6cqw;
		}
	`;

	tenderTextRef = createRef();

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.lang = state.status.lang;
		this.state = state;
		this.debug = state.status.debug;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
	}

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('copyToClipboard', () => this.copyToClipboard());
	}

	disconnectedCallback() {
		window.removeEventListener('copyToClipboard', () => this.copyToClipboard());
		super.disconnectedCallback();
	}
	firstUpdated(changedProperties) {
		const tenderText = this.tenderTextRef.value.innerText;
		const cleanedText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		store.dispatch(saveTenderText({tenderText: cleanedText}));
	}

	copyToClipboard() {
		let tenderText = this.tenderTextRef.value.innerText;
		// clean lineBreaks and spaces
		tenderText = tenderText.replace(/\n(\s*)\n/gm, '\n\n').replace(/ +/g, ' ');
		navigator.clipboard.writeText(tenderText).then(
			() => {
			},
			(err) => {
				console.error('Async: Could not copy text: ', err);
			});
	}

	// shortcut for translated text
	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	onlyNone(key) {
		if (!key.includes('::')) {
			key = 'root::' + key;
		}
		const attributeOptions = selectSectionConfig(this.state)[0].attributeOptions ?? null;
		const checkForOnlyNone = attributeOptions[key]?.doNotShowWhenNoneIsOnlyOption ?? false;
		return checkForOnlyNone && hasOnlyThisFeatureValue(key, 'None', this.spcConfiguration);
	}
	render() {
		const s = this.state;

		const dimensionsLabel = this.t('TE_Dimensions', 'D');
		const dimensionDiameter = selectDimensionDiameter(s);
		const dimensionDepth = selectDimensionDepth(s);
		const dimensionLength = selectDimensionLength(s);
		const dimensionWidth = selectDimensionWidth(s);
		const dimensionHeight = selectDimensionHeight(s);
		const isDimensionsPresent = dimensionsLabel || dimensionDepth || dimensionWidth || dimensionHeight;

		const applicableStandards = [
			{key: 'LU_IP.IP66', labelContext: 'DV.S', valueContext: 'DV.S'},
			{key: 'LU_IK.10', labelContext: 'DV.S', valueContext: 'DV.S'},
			{key: 'EB_ElectricalProtectionClass_1', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'LU_ENEC_CertificateDescription.ENEC', labelContext: 'DV.S', valueContext: 'DV.S'},
			{key: 'LU_CE.CE', labelContext: 'DV.S', valueContext: 'DV.S'}
		];

		const aggregatedStandards = filterAndJoin(applicableStandards.map(data => this.t(data.key, data.valueContext)), ', ');
		const filteredStandards = applicableStandards.filter(value => value !== undefined && value !== '');
		const isStandardsPresent = filteredStandards.length > 0;

		const dimmedLumenValue = parseInt(selectDimmLevel(s), 10);


		const powerLabel = {
			en: 'Power',
			fr: 'Puissance',
			de: 'Leistung',
		};
		const currentLabel = {
			en: 'Current',
			fr: 'Actuel',
			de: 'Stromstärke',
		};

		let maxLumenPerWatt = 0;
		let maxLumenValue = 0;
		if (this.spcConfiguration) {
			maxLumenValue = parseInt(getValueOfFeature('root::LE_DimmingLevel_1', this.spcConfiguration), 10);
		}
		const dimmedLumen = parseInt(selectDimmLevel(s), 10);
		const dimmedLumenPerWatt = Math.round(
			dimmedLumen /
			calculatePower(dimmedLumen, selectCoefficients(s).formular2.coefficients) * 100
		) / 100;

		let maxWatt = parseInt(this.c('REDTAB.RETURN_EB_Wattage_Max'), 10) * 100;
		if (selectCoefficients(s)) {
			maxWatt = calculatePower(maxLumenValue, selectCoefficients(s).formular2.coefficients);
		}
		maxLumenPerWatt = Math.round((maxLumenValue / maxWatt) * 100 ) / 100;

		return html`
			<div ${ref(this.tenderTextRef)}>

				<p>
					<!-- HS_HousingSize HS_HousingType HS_Construction-->
					${filterAndJoin([
						this.t('HS_HousingSize', 'FV.S', null, '', ' '),
						this.t('HS_HousingType', 'FV.S', null, '', ''),
						this.t('HS_Construction', 'FV.S', null, ', '),
					], ' ')}
				</p>

				<p>
					<!-- LE_LightDistribution_1 LE_ColourTemperature_1 LE_DimmingLevel_1 LE_CRI REDTAB.RETURN_EB_Wattage_Max-->
					${filterAndJoin([
						this.t('LE_LightDistribution_1', 'FV.S'),
						this.t('LE_ColourTemperature_1', 'FV.S'),
						this.t('LE_CRI', 'D', null, '', ' ') + this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV')
					], ', ')}
				</p>


				<p>
					${msg('max')}. <trans-label slot="label" key="LE_DimmingLevel_1" context="F"></trans-label>
					<spc-values-label attributeKey="LE_DimmingLevel_1" display="selected" context="FV.S"></spc-values-label>,
					${when(maxWatt && maxWatt > 0, () => html`${msg('max')}. <trans-label slot="label" key="LU_SystemWattage" context="D"></trans-label> ${maxWatt} W`)}
					${when(!isNaN(maxLumenPerWatt) && maxLumenPerWatt !== Infinity, () => html` (${maxLumenPerWatt} lm/W)`)}
					<br>

				${when(selectHasCoefficients(s) && dimmedLumen !== maxLumenValue,
					() => html`
							${msg('dimmed luminous flux')} ${dimmedLumen} lm,
							${msg('dimmed power')} ${calculatePower(dimmedLumen, selectCoefficients(s).formular2.coefficients)} W
							(${dimmedLumenPerWatt} lm/W)
							<br>
					`)}

				${when(selectHasCoefficients(s),
					() => html`
							${currentLabel[this.lang]} ${calculateCurrent(dimmedLumen, selectCoefficients(s).formular1.coefficients)} mA
							<br>
					`)}
				</p>

				<p>
					<!-- LU_Product_Description-->
					<trans-label key="LU_Product_Description" context="D.L"></trans-label>
				</p>

				<p>
					<!-- Lichtprofil -->
					${this.t('HS_CircularBaseProfile', 'F', null, '', ':')}
					${this.t('HS_CircularBaseProfile', 'FV.L')}
				</p>

				<p>
					<trans-label key="LE_LightEngineDescription" context="D"></trans-label>:
					<!-- DescriptiveAttributeReference.LE_LightEngineDescription LE_OpticalVersion_1 HS_LightEmission LE_ReflectorColour_1-->
					${filterAndJoin([
						this.t('LE_LightEngineModuleSize_1', 'FV'),
						this.t('LE_LightEngineModuleSize_1', 'FV.L'),
						this.t('LE_ReflectorColour_1', 'FV.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_ReflectorMaterial'), 'DV.L'),
						this.t('LE_LightEngineDescription', 'D.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineDescription'), 'DV'),
						this.t('LE_OpticalVersion_1', 'FV.L'),
						this.t('HS_UpLight', 'FV.L'),
						selectRegion(s) === 'fra' ? this.t('HS_LightEmissionCA', 'FV.S') : '',
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineDescription'), 'DV.L'),
					], ', ')}
				</p>

				<p>
					<!-- DescriptiveAttributeReference.LU_LuminaireMaterial  -->
					${filterAndJoin([
						this.t(this.c('DescriptiveAttributeReference.LU_LuminaireMaterial'), 'DV.L'),
					], ', ')}
				</p>

				<p>
					<!-- HS_HousingDescription DescriptiveAttributeReference.LE_LightEngineCover HS_HousingPowerConnection HS_Attachement.Avanza -->
					${filterAndJoin([
						this.t('HS_HousingDescription', 'D.L'),
						this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L'),
						this.t('HS_HousingPowerConnection', 'FV.S'),
						this.t('HS_HousingPowerConnection', 'FV.S') !== null ? this.t(this.c('DescriptiveAttributeReference.LU_CableType'), 'DV') : '',
						this.t(this.c('DescriptiveAttributeReference.HS_Attachement'), 'DV.L'),
						!this.onlyNone('root::HS_Base') ? this.t('HS_Base', 'FV.L') : '',
					], ', ')}
				</p>

				<p>
					<!-- LU_Maintanance -->
					${this.t('LU_Maintanance', 'D', null, '', ':')}
					${this.t('LU_Maintanance', 'D.L', null, '', '')}
					${this.t(this.c('DescriptiveAttributeReference.LU_Maintanance'), 'DV.L')}
				</p>

				<p>
					<!-- EB_Control_1 EB_ControlNoWire_1 EB_ControlWire_1-->
					${this.t('EB_ControlNoWire_1', 'F', null, '', ':')}
					${when(this.t('EB_Control_1', 'FV.S'),
						() => html`${this.t('EB_Control_1', 'FV.S')}`,
						() => html`
							${filterAndJoin([
								this.t('EB_ControlNoWire_1', 'FV.S'),
								this.t('EB_ControlWire_1', 'FV.S'),
							], ' + ')}
						`)
					}

				</p>
				<p>
					<!-- EB_ConstantLumenTechnology_1 -->
					${this.t('EB_ConstantLumenTechnology_1', 'F', null, '', ':')}
					${this.t('EB_ConstantLumenTechnology_1', 'FV.L')}
				</p>
				${when(!this.onlyNone('root::EB_Zhaga_1'), () => html`
					<p>
						<!-- EB_Zhaga_1 -->
						${this.t('EB_Zhaga_1', 'F', null, '', ':')}
						${this.t('EB_Zhaga_1', 'FV.L')}
					</p>
				`)}
				<p>
					<!-- LU_SurgeProtection LU_TechnicalRegion LU_AmbientTemperature-->
					${when(this.t('LU_SurgeProtection.10 kV', 'DV.S', null, '', ''),
						() => html`
							${this.t('LU_SurgeProtection', 'D')}
							${this.t('LU_SurgeProtection.10 kV', 'DV.S', null, '', ',')}
						`)}
					${when(this.t('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV', null, '', ''),
						() => html`
							${this.t('LU_TechnicalRegion', 'D')}
							${this.t('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV', null, '', '')}
						`)}
					${when(this.c('DescriptiveAttributeReference.LU_AmbientTemperature'),
						() => html`<br>
							${this.t('LU_AmbientTemperature', 'D', null, '')}
							${this.t(this.c('DescriptiveAttributeReference.LU_AmbientTemperature'), 'DV.S')}
						`)}
				</p>

				<p>
					<!-- HS_HousingColour -->
					${this.t('HS_HousingColour', 'F', null, '', ':')}
					${this.t('HS_HousingColour', 'FV')}
				</p>
				<p>
					<!-- HS_HousingCoating -->
					${this.t('HS_HousingCoating', 'F', null, '', ':')}
					${this.t('HS_HousingCoating', 'FV')}
				</p>
				${when(!this.onlyNone('root::HS_Base'), () => html`
					<p>
						<!-- HS_Base -->
						${filterAndJoin([
							this.t('HS_Base', 'F'),
							this.t('HS_Base', 'FV.S')
						], ': ')}
					</p>
				`)}

				<p>
					${filterAndJoin([
						this.t(this.c('DescriptiveAttributeReference.LU_IK'), 'DV.S'),
						this.t(this.c('DescriptiveAttributeReference.LU_IP'), 'DV.S'),
						this.t('EB_ElectricalProtectionClass_1', 'FV.S'),
						this.t(this.c('DescriptiveAttributeReference.LU_ENEC_CertificateDescription'), 'DV.S'),
						this.t(this.c('DescriptiveAttributeReference.LU_CE'), 'DV.S')
					], ', ')}
				</p>


				<p>
					<!-- HS_HousingHeight LU_LuminaireWeight HS_WindSurfaceArea-->
					${filterAndJoin([
						this.t('HS_HousingHeight', 'F') +
						this.t('HS_HousingHeight', 'FV', null, ' ',),
						this.t('LU_LuminaireWeight', 'D', null, ' ', ' ') +
						selectWeight(s) +
						this.t('LU_LuminaireWeightUnit', 'D', null, ' '),
						this.t('HS_WindSurfaceArea', 'D', null, '', ' ') +
						selectWindsurfacearea(s) +
						this.t('HS_WindSurfaceAreaUnit.M2', 'DV.S', null, ' ')
					], ', ')}
				</p>

				<p>
					<trans-label class="label" key="TE_Dimensions" context="D"></trans-label>:

					${dimensionDiameter ? html`
						<trans-label slot="label" class="value" key="HS_HousingDiameter" context="D" suffix=":"></trans-label>
						${dimensionDiameter}
						<trans-label slot="label" class="value" key="HS_HousingDiameterUnit.mm" context="DV"></trans-label>,
					` : ''}

					${dimensionDepth ? html`
						<trans-label slot="label" class="value" key="HS_HousingLength" context="D" suffix=":"></trans-label>
						${dimensionDepth}
						<trans-label slot="label" class="value" key="HS_HousingLengthUnit.mm" context="DV"></trans-label>,
					` : ''}

					${dimensionLength ? html`
						<trans-label slot="label" class="value" key="HS_HousingLength" context="D" suffix=":"></trans-label>
						${dimensionLength}
						<trans-label slot="label" class="value" key="HS_HousingLengthUnit.mm" context="DV"></trans-label>,
					` : ''}

					${dimensionWidth ? html`
						<trans-label slot="label" class="value" key="HS_HousingWidth" context="D" suffix=":"></trans-label>
						${dimensionWidth}
						<trans-label slot="label" class="value" key="HS_HousingWidthUnit.mm" context="DV"></trans-label>,
					` : ''}

					${dimensionHeight ? html`
									<trans-label slot="label" class="value" key="HS_HousingHeight" context="D" suffix=":"></trans-label>
									${dimensionHeight}
									<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>
								` : ''}
				</p>

				<p>
					<!-- LU_Warranty -->
					${this.t('LU_Warranty', 'D', null, '', ':')}
					${this.t('LU_Warranty', 'D.L', null, '', '')}
				</p>

				<p>
					<br>
					${this.t('TE_Manufacturer', 'D', null, '', ':')} Selux
				</p>



					<!--
				<p>
					Typ: ${selectArticleCodeJoinedComponents(s)}<br>
				</p>
				-->
				${when(this.c('REDTAB.RETURN_LU_LegacyArticleNumberGerman'), () => html`
					<p>
						${this.c('REDTAB.RETURN_LU_LegacyArticleNumberGerman')}
					</p>
				`)}
				${when(this.state.status.features.variantCodes,
					() => html`
						<p class="info">
							${repeat(
								selectVariantCodeTypes(s),
								(variantCode) => html`${variantCode.id}: ${variantCode.code}<br>`
							)}
						</p>`
				)}
				<p>
					<trans-label .key="${this.c('DescriptiveAttributeReference.LU_Accessories')}" context="DV.L"></trans-label>
				</p>
			</div>
		`;
	}
}
